import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { gql } from '../../../graphql/client';
import {
  M_DELETE_VIEW_HIERARCHY,
  M_INSERT_VIEW,
  M_INSERT_VIEW_VIEW,
  M_SET_MAIN_VIEW,
} from '../../../graphql/operations';
import type { View } from '../../../types/forge';
import { composeSnapshot, type AppDataShape } from '../preview/composeSnapshot';
import { resolveDropParent } from '../dragDrop/resolveDropParent';
import { useEditorStore } from '../store/editorStore';

/**
 * Canvas — true-WYSIWYG iframe renderer.
 *
 * The iframe loads `/preview.html`, which hosts the actual engine running
 * in RN-Web. We post the current snapshot to it; it posts back which view
 * the user clicked. The same engine code that renders production binaries
 * also renders here.
 *
 * Drag-from-palette is intercepted at the iframe boundary: we attach
 * dragOver / drop handlers to the iframe wrapper element and translate
 * a drop into M_INSERT_VIEW + M_INSERT_VIEW_VIEW under whichever view the
 * cursor was hovering. (Identifying the hovered view from outside the
 * iframe is tricky — for v1 we drop at root or under the currently-
 * selected view. Phase 5.5 polish: forward dragmove events into the
 * iframe via postMessage to highlight the target.)
 */
export function Canvas({ appData }: { appData: AppDataShape }) {
  const appId = appData.FrontendApp_by_pk?.id;
  const rootId = appData.FrontendApp_by_pk?.main_view_id;
  const draggedType = useEditorStore((s) => s.draggedViewType);
  const endDrag = useEditorStore((s) => s.endDrag);
  const select = useEditorStore((s) => s.selectView);
  const selectedId = useEditorStore((s) => s.selectedViewId);
  const queryClient = useQueryClient();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [hoverDrop, setHoverDrop] = useState(false);

  const snapshot = useMemo(() => composeSnapshot(appData), [appData]);

  /* ----------------------- Listen for iframe messages ----------------------- */

  useEffect(() => {
    function handle(e: MessageEvent) {
      const msg = e.data;
      if (!msg || typeof msg !== 'object' || !('type' in msg)) return;
      if (msg.type === 'engine:ready') {
        setIframeReady(true);
      } else if (msg.type === 'engine:viewClicked') {
        select(msg.viewId);
      }
    }
    window.addEventListener('message', handle);
    return () => window.removeEventListener('message', handle);
  }, [select]);

  /* --------------------- Push snapshot when ready / changes ----------------- */

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow || !iframeReady || !snapshot) return;
    iframe.contentWindow.postMessage(
      { type: 'engine:init', snapshot, selectedViewId: selectedId ?? null },
      '*',
    );
    // Subsequent updates: just re-init (the preview rebuilds the repository
    // on every snapshot anyway, which is cheap).
  }, [iframeReady, snapshot, selectedId]);

  /* ---------------------------- Drag-drop on canvas -------------------------- */

  const insertView = useMutation({
    mutationFn: async ({ parentId }: { parentId: string | null }) => {
      const sort = 0;
      const created = await gql<{ insert_View_one: View }>(M_INSERT_VIEW, {
        appId,
        type: draggedType,
        name: `${draggedType} ${Date.now() % 10000}`,
        sort,
      });
      const newId = created.insert_View_one.id;
      if (parentId) {
        await gql(M_INSERT_VIEW_VIEW, { parentId, childId: newId, sort });
      } else {
        await gql(M_SET_MAIN_VIEW, { appId, viewId: newId });
      }
      return newId;
    },
    onSuccess(newId) {
      queryClient.invalidateQueries({ queryKey: ['app-data', appId] });
      select(newId);
    },
  });

  /*
   * Compute the resolved drop target for the *current* drag. We
   * memo this so the hint label below the canvas can show exactly
   * where the drop will land — keeps the label and the actual drop
   * in lockstep.
   */
  const dropParentId = useMemo<string | null>(() => {
    if (!draggedType) return null;
    return resolveDropParent(draggedType, selectedId, rootId ?? null, appData.View);
  }, [draggedType, selectedId, rootId, appData.View]);

  const handleDragOver = (e: React.DragEvent) => {
    if (!draggedType) return;
    e.preventDefault();
    setHoverDrop(true);
  };
  const handleDragLeave = () => setHoverDrop(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedType) return;
    insertView.mutate({ parentId: dropParentId });
    setHoverDrop(false);
    endDrag();
  };

  /* -------------------- Keyboard delete on selected view -------------------- */

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Backspace' && e.key !== 'Delete') return;
      if (!selectedId || selectedId === rootId) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      const v = appData.View.find((x) => x.id === selectedId);
      const childCount = v?.child_views?.length ?? 0;
      const msg = childCount > 0
        ? `Delete "${v?.name}" and ${childCount} descendant${childCount > 1 ? 's' : ''}?`
        : `Delete "${v?.name}"?`;
      if (window.confirm(msg)) {
        gql(M_DELETE_VIEW_HIERARCHY, { viewId: selectedId }).then(() => {
          queryClient.invalidateQueries({ queryKey: ['app-data', appId] });
          select(null);
        });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedId, rootId, appData.View, appId, queryClient, select]);

  /* ------------------------------- Render ---------------------------------- */

  if (!rootId) {
    return (
      <div
        style={{ ...styles.empty, ...(hoverDrop ? styles.emptyHover : {}) }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        Drop a component here to start
      </div>
    );
  }

  return (
    <div style={styles.canvas}>
      <div
        style={{
          ...styles.deviceFrame,
          ...(hoverDrop ? styles.deviceFrameHover : {}),
          position: 'relative',
        }}
      >
        <iframe
          ref={iframeRef}
          src="/preview.html"
          title="Forge preview"
          style={styles.iframe}
        />
        {/*
          Drag overlay — only present while a palette drag is in progress.
          Without this, the iframe captures dragover/drop events and the
          parent's handlers never fire. The overlay covers the iframe with
          an absolutely-positioned transparent div that intercepts the
          drag, lets us call e.preventDefault() to enable the drop, and
          fires our handlers from the parent context.
        */}
        {draggedType && (
          <div
            style={styles.dragOverlay}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          />
        )}
      </div>
      <div style={styles.hint}>
        {draggedType ? (
          <>
            Drop to insert <strong>{draggedType}</strong>
            {dropParentId
              ? <> inside <strong>{appData.View.find(v => v.id === dropParentId)?.name ?? 'root'}</strong></>
              : ' as the new main view'}
          </>
        ) : (
          'Click any view to select · ⌫ to delete · drag from palette to add'
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  canvas: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 24,
    gap: 12,
  },
  deviceFrame: {
    width: 390,
    height: 844, // iPhone 14 Pro
    border: '1px solid #CBD5E1',
    borderRadius: 24,
    overflow: 'hidden',
    backgroundColor: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    transition: 'border-color 120ms',
  },
  deviceFrameHover: { borderColor: '#4F46E5', borderWidth: 2 },
  iframe: { width: '100%', height: '100%', border: 'none', display: 'block' },
  dragOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(79, 70, 229, 0.04)',
    border: '2px dashed rgba(79, 70, 229, 0.3)',
    borderRadius: 24,
    zIndex: 10,
  },
  empty: {
    width: 360,
    padding: '60px 20px',
    border: '2px dashed #CBD5E1',
    borderRadius: 12,
    textAlign: 'center',
    color: '#64748B',
    backgroundColor: '#fff',
    margin: '40px auto',
  },
  emptyHover: { borderColor: '#4F46E5', backgroundColor: '#EEF2FF' },
  hint: { fontSize: 11, color: '#94A3B8' },
};
